import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { useQueryClient } from '@tanstack/react-query';
import { Checkbox, Spin } from 'antd';
import MultiSelectHint from 'components/common/MultiSelectHint/MultiSelectHint';
import SearchInput from 'components/common/SearchInput/SearchInput';
import {
  M_ADJUVANT_QUERY_KEY,
  M_BIOSTIMULANT_QUERY_KEY,
  M_CHEMICAL_QUERY_KEY,
  M_CROPS_QUERY_KEY,
  M_OPERATION_QUERY_KEY,
  M_SEARCH_ADJUVANT_QUERY_KEY,
  M_SEARCH_BIOSTIMULANT_QUERY_KEY,
  M_SEARCH_CHEMICAL_QUERY_KEY,
  M_SEARCH_CROPS_QUERY_KEY,
  M_SEARCH_OPERATION_QUERY_KEY,
  M_SEARCH_SOILINPUT_QUERY_KEY,
  M_SOILINPUT_QUERY_KEY,
} from 'constants/queryKeys';
import {
  SEED_STEP_GROUP_KEY,
  SEEDDRESS_STEP_GROUP_KEY,
  STEP_SELECTOR_EMPTY_OPTIONS_MESSAGE,
  STEP_SELECTOR_FETCHING_LABEL,
  STEP_SELECTOR_NO_STEP_TYPES_MESSAGE,
  STEP_SELECTOR_SAVE_BLOCKED_BY_SEED_TREATMENT_TOOLTIP,
  STEP_SELECTOR_SEARCH_PLACEHOLDER,
} from 'constants/uiConstants';
import { useFetchGUnits } from 'hooks/useFetchGUnits';
import { IInputStepSeedDress } from 'interfaces/IInputStep';
import type { IInputTask } from 'interfaces/IInputTask';
import { InputTaskType } from 'interfaces/IInputTask';
import MergeClassNames from 'utils/MergeClassNames';

import { selectActiveGOrgId } from '../../../../redux/selectors/GlobalCatalogueSelectors';

import { AddProductKind, getAddProductKind } from './addRecord';
import AddRecordFlyout from './AddRecordFlyout';
import AddRecordRow from './AddRecordRow';
import AddVarietyFlyout from './AddVarietyFlyout';
import CatalogueOptionList from './CatalogueOptionList';
import CategoryTabStrip from './CategoryTabStrip';
import OptionRow from './OptionRow';
import { createPickerFlyoutRules, isModifierClick } from './pickerFlyoutRules';
import SeedCropVarietyPicker from './SeedCropVarietyPicker';
import SeedTreatmentProductPicker from './SeedTreatmentProductPicker';
import SeedTreatmentTwoPanel from './SeedTreatmentTwoPanel';
import SeedVarietyTwoPanel from './SeedVarietyTwoPanel';
import SelectionActionBar from './SelectionActionBar';
import SelectionChips from './SelectionChips';
import { getSeedTreatmentOptionDisplayLabel } from './stepCalculations';
import {
  FLYOUT_ADD_BUTTON_ATTRIBUTE,
  getStepOptionId,
  getStepSelectorGroupsForTask,
  PICKER_OPTION_ID_ATTRIBUTE,
  stepOptionsMatch,
  StepSelectorGroup,
  StepSelectorOption,
} from './stepConfig';
import { StepSelection, toStepSelection } from './stepFactory';
import useCheckedSelections, {
  buildSaveSelections,
} from './useCheckedSelections';
import usePickerFocusTrap from './usePickerFocusTrap';
import useSeedTreatmentFlyout from './useSeedTreatmentFlyout';
import useSeedVarietyFlyout from './useSeedVarietyFlyout';
import { useStepCatalogueOptions } from './useStepCatalogueOptions';
import { useStepSelectorKeyboard } from './useStepSelectorKeyboard';

interface StepInputSelectorProps {
  taskType: InputTaskType;
  onClose: () => void;
  onSelect: (payload: StepSelection[]) => void;
  initialCategoryKey?: string;
  initialSearchText?: string;
  enableMultiSelect?: boolean;
  initialCropGlobalId?: string | null;
  initialCropName?: string | null;
  initialVarietyGlobalId?: string | null;
  initialVarietyName?: string | null;
  task?: IInputTask;
  initialSeedDressStep?: IInputStepSeedDress;
  /** When set, all category tabs other than the currently active one are
   *  disabled and show this string as a tooltip explaining why. */
  lockedCategoryReason?: string;
  /** When set, only these category tabs are offered (e.g. fertilisers only
   *  in the Nutrition lens, OP-2523); with just one, the tab strip is hidden. */
  categoryKeys?: string[];
  /**
   * Tabs shown but disabled, keyed by category, each with the reason as its
   * tooltip — e.g. Production once a Production task has its product
   * (OP-2399).
   */
  lockedCategoryReasons?: Record<string, string>;
  /**
   * Offers "Add '<text>'" when a search finds nothing, to create the record in
   * the user's own list (OP-2498). On in the tasks grid only.
   */
  enableAddRecord?: boolean;
}

// MSearch caches dropped on unmount so re-opening starts fresh, not from 5-minute-old results.
const M_SEARCH_QUERY_KEYS = [
  M_SEARCH_CHEMICAL_QUERY_KEY,
  M_SEARCH_ADJUVANT_QUERY_KEY,
  M_SEARCH_OPERATION_QUERY_KEY,
  M_SEARCH_SOILINPUT_QUERY_KEY,
  M_SEARCH_BIOSTIMULANT_QUERY_KEY,
  M_SEARCH_CROPS_QUERY_KEY,
];

// The user's own lists, refetched when the picker opens so records added on
// another device show up (OP-2498).
const M_LIST_QUERY_KEYS = [
  M_CHEMICAL_QUERY_KEY,
  M_ADJUVANT_QUERY_KEY,
  M_OPERATION_QUERY_KEY,
  M_SOILINPUT_QUERY_KEY,
  M_BIOSTIMULANT_QUERY_KEY,
  M_CROPS_QUERY_KEY,
];

const StepInputSelector: React.FC<StepInputSelectorProps> = ({
  taskType,
  onClose,
  onSelect,
  initialCategoryKey,
  initialSearchText,
  enableMultiSelect = false,
  initialCropGlobalId,
  initialCropName,
  initialVarietyGlobalId,
  initialVarietyName,
  task,
  initialSeedDressStep,
  lockedCategoryReason,
  categoryKeys,
  lockedCategoryReasons,
  enableAddRecord = false,
}) => {
  // GC tenant org read from Redux rather than drilled down, as InputStepDetailsModal does.
  const gOrgId = useSelector(selectActiveGOrgId);
  // Org-wide reference data already warmed elsewhere, so this reads React Query's cache.
  const { data: gUnits = [] } = useFetchGUnits(gOrgId);
  const queryClient = useQueryClient();

  const groups = useMemo(
    () =>
      getStepSelectorGroupsForTask(taskType).filter(
        (group) => !categoryKeys || categoryKeys.includes(group.key)
      ),
    // categoryKeys is compared by value, so an inline array doesn't refilter every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [taskType, categoryKeys?.join()]
  );
  const [activeCategoryKey, setActiveCategoryKey] = useState<string>(
    initialCategoryKey ?? groups[0]?.key ?? ''
  );
  const [searchText, setSearchText] = useState(initialSearchText ?? '');
  const [appliedSearchText, setAppliedSearchText] = useState('');
  const [highlightedItem, setHighlightedItem] =
    useState<StepSelectorOption | null>(null);
  // One flag for whichever flyout (or edit two-panel) the active tab shows.
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  // The Add form beside the picker, with the search text it was opened from.
  const [addRequest, setAddRequest] = useState<
    | { kind: AddProductKind; name: string }
    | { kind: 'variety'; name: string; crop: StepSelectorOption }
    | null
  >(null);

  const optionsContainerRef = useRef<HTMLDivElement | null>(null);
  // Flyout anchor: the flyouts portal out of this subtree and position beside the whole picker.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const pendingKeyboardHighlightRef = useRef(false);
  // Skips the tab-change reset on mount; it should only run on an actual tab switch.
  const categoryInitializedRef = useRef(false);

  const activeGroup = useMemo(
    () => groups.find((group) => group.key === activeCategoryKey) ?? groups[0],
    [activeCategoryKey, groups]
  );
  const isSeedTab = activeGroup?.key === SEED_STEP_GROUP_KEY;
  const addRecordKind =
    enableAddRecord && activeGroup ? getAddProductKind(activeGroup.key) : null;
  const seedTreatmentTask =
    activeGroup?.key === SEEDDRESS_STEP_GROUP_KEY ? task : undefined;

  const {
    isCatalogueGroup,
    catalogue,
    filteredOptions,
    clearAllSearches,
    onSearchForKey,
  } = useStepCatalogueOptions(
    gOrgId,
    activeGroup,
    appliedSearchText,
    taskType,
    initialCropGlobalId ?? null
  );

  const selections = useCheckedSelections(activeCategoryKey);
  const {
    checkedItemsByCategory,
    checkedItems,
    checkedSeeds,
    totalCheckedCount,
    isMultiSelectMode,
    isMultiSelectLocked,
    hasHadSelection,
    toggleCheckedItem,
    removeCheckedItem,
    lockMultiSelect,
    resetMultiSelect,
  } = selections;

  const commitSingleSelect = (item: StepSelectorOption) => {
    if (!activeGroup) return;
    // Re-confirming the saved seed + variety closes without an update.
    const unchangedSeed =
      initialCropGlobalId &&
      item.globalId === initialCropGlobalId &&
      (item.globalId2 ?? null) === (initialVarietyGlobalId ?? null);
    if (!unchangedSeed) {
      onSelect([toStepSelection(item, activeGroup.stepType)]);
    }
    onClose();
  };

  const flyoutHost = {
    enableMultiSelect,
    filteredOptions,
    highlightedItem,
    setHighlightedItem,
    isFlyoutOpen,
    setIsFlyoutOpen,
    selections,
    rules: createPickerFlyoutRules({
      enableMultiSelect,
      isFlyoutOpen,
      highlightedItem,
      setHighlightedItem,
      selections,
    }),
    commitSingleSelect,
  };
  const seedVariety = useSeedVarietyFlyout({
    ...flyoutHost,
    isSeedTab,
    initialCropGlobalId,
    initialCropName,
    initialVarietyGlobalId,
    initialVarietyName,
  });
  const seedTreatment = useSeedTreatmentFlyout({
    ...flyoutHost,
    seedTreatmentTask,
    initialSeedDressStep,
  });

  const {
    reset: resetSeedVariety,
    resetPreselection: resetSeedPreselection,
    applyEditPreselection: applySeedPreselection,
  } = seedVariety;
  const {
    reset: resetSeedTreatment,
    resetPreselection: resetSeedTreatmentPreselection,
    applyEditPreselection: applySeedTreatmentPreselection,
  } = seedTreatment;
  const resetFlyoutState = useCallback(() => {
    setIsFlyoutOpen(false);
    setAddRequest(null);
    resetSeedVariety();
    resetSeedTreatment();
  }, [resetSeedVariety, resetSeedTreatment]);

  useEffect(() => {
    const fallbackKey = groups[0]?.key ?? '';
    const initialActiveKey =
      initialCategoryKey &&
      groups.some((group) => group.key === initialCategoryKey)
        ? initialCategoryKey
        : fallbackKey;

    setActiveCategoryKey(initialActiveKey);
    setSearchText(initialSearchText ?? '');
    setAppliedSearchText('');
    resetMultiSelect();
    resetFlyoutState();
    if (optionsContainerRef.current) {
      optionsContainerRef.current.scrollTop = 0;
    }
    // Highlights reset on every open; the pre-selection effects re-apply them.
    setHighlightedItem(null);
    resetSeedPreselection();
    resetSeedTreatmentPreselection();
  }, [
    resetMultiSelect,
    resetFlyoutState,
    resetSeedPreselection,
    resetSeedTreatmentPreselection,
    groups,
    initialCategoryKey,
    initialSearchText,
    clearAllSearches,
    initialCropGlobalId,
    initialVarietyGlobalId,
    taskType,
  ]);

  // Declared after the open reset above so the reset can never undo a pre-selection.
  useEffect(() => {
    applySeedPreselection();
    applySeedTreatmentPreselection();
  }, [applySeedPreselection, applySeedTreatmentPreselection]);

  useEffect(() => {
    if (!enableAddRecord) return;
    M_LIST_QUERY_KEYS.forEach((queryKey) =>
      queryClient.invalidateQueries({ queryKey: [queryKey] })
    );
  }, [enableAddRecord, queryClient]);

  useEffect(
    () => () => {
      M_SEARCH_QUERY_KEYS.forEach((queryKey) =>
        queryClient.removeQueries({ queryKey: [queryKey] })
      );
    },
    [queryClient]
  );

  useEffect(() => {
    if (!categoryInitializedRef.current) {
      categoryInitializedRef.current = true;
      return;
    }
    resetFlyoutState();
    if (optionsContainerRef.current) {
      optionsContainerRef.current.scrollTop = 0;
    }
  }, [activeCategoryKey, resetFlyoutState]);

  useEffect(() => {
    if (!pendingKeyboardHighlightRef.current) return;
    if (filteredOptions.length === 0) return;
    setHighlightedItem(filteredOptions[0]);
    pendingKeyboardHighlightRef.current = false;
  }, [filteredOptions]);

  // When a flyout closes with focus inside it, focus would drop to the page; keep it on the picker.
  const wasFlyoutOpenRef = useRef(false);
  useEffect(() => {
    const active = document.activeElement;
    if (
      wasFlyoutOpenRef.current &&
      !isFlyoutOpen &&
      (!active || active === document.body)
    ) {
      rootRef.current?.focus({ preventScroll: true });
    }
    wasFlyoutOpenRef.current = isFlyoutOpen;
  }, [isFlyoutOpen]);

  const checkedCountByKey = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(checkedItemsByCategory).map(([key, items]) => [
          key,
          items.length,
        ])
      ),
    [checkedItemsByCategory]
  );

  const checkedGlobalIds = useMemo(
    () =>
      new Set(
        checkedItems
          .map((item) => item.globalId)
          .filter((id): id is string => !!id)
      ),
    [checkedItems]
  );

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setAppliedSearchText(value);
    if (value) {
      // Only search the active tab — other tabs are searched lazily on switch.
      onSearchForKey(activeCategoryKey, value);
    } else {
      // Clearing the search box resets all catalogues to browse mode.
      clearAllSearches();
    }
  };

  // Clicking back into the picker search closes an open add-flow flyout.
  const handleSearchMouseDown = () => {
    if (enableMultiSelect && isFlyoutOpen) seedVariety.close();
  };

  // Keyboard tab switches highlight the new tab's first option once it loads; clicks don't.
  const changeCategory = (
    group: StepSelectorGroup,
    highlightFirst: boolean
  ) => {
    pendingKeyboardHighlightRef.current = highlightFirst;
    setActiveCategoryKey(group.key);
    setHighlightedItem(null);
    // Trigger MSearch for the newly active tab if a search is in progress.
    if (searchText) {
      onSearchForKey(group.key, searchText);
    }
  };

  // Plain and catalogue lists: Ctrl/Cmd+click (or multi-select) checks; otherwise commits.
  const handleOptionClick = (
    item: StepSelectorOption,
    event: React.MouseEvent
  ) => {
    pendingKeyboardHighlightRef.current = false;
    if (enableMultiSelect && (isMultiSelectMode || isModifierClick(event))) {
      lockMultiSelect();
      toggleCheckedItem(item);
      setHighlightedItem(item);
      return;
    }
    commitSingleSelect(item);
  };

  // Saving now would drop the open Seed Treatment flyout's edits, so Save waits for Add or close.
  const saveBlockedReason = seedTreatment.hasUnconfirmedChanges
    ? STEP_SELECTOR_SAVE_BLOCKED_BY_SEED_TREATMENT_TOOLTIP
    : undefined;

  // Save only exists in multi-select (the action bar and Shift+Enter), so it always saves the checked items.
  const handleSave = () => {
    if (saveBlockedReason) {
      // Points the user at the fix: Add (or Escape to close) in the open flyout.
      document
        .querySelector<HTMLElement>(`[${FLYOUT_ADD_BUTTON_ATTRIBUTE}]`)
        ?.focus({ preventScroll: true });
      return;
    }
    const saveSelections = buildSaveSelections(checkedItemsByCategory, groups);
    if (saveSelections.length === 0) return;
    onSelect(saveSelections);
    onClose();
  };

  // Removing a chip unchecks like a row click: that item's own flyout closes, another's stays.
  const handleRemoveChip = (id: string) => {
    const item = checkedItems.find(
      (checked) => getStepOptionId(checked) === id
    );
    if (item && flyoutHost.rules.isFlyoutOpenFor(item)) {
      if (isSeedTab) seedVariety.close();
      else seedTreatment.close();
    }
    removeCheckedItem(id);
  };

  const handleCancel = () => {
    resetMultiSelect();
    resetFlyoutState();
    onClose();
  };

  const isSaveDisabled = !activeGroup || totalCheckedCount === 0;

  useStepSelectorKeyboard({
    isMultiSelectMode,
    isMultiSelectLocked,
    enableMultiSelect,
    isSeedGroup: isSeedTab,
    isSeedTreatmentPicker: !!seedTreatmentTask && enableMultiSelect,
    isSeedTreatmentTab: !!seedTreatmentTask,
    areCategoriesLocked: !!lockedCategoryReason,
    isCatalogueGroup,
    highlightedItem,
    filteredOptions,
    groups,
    activeCategoryKey,
    searchText,
    totalCheckedCount,
    isSeedFlyoutOpen: isFlyoutOpen,
    pendingSeedVarieties: seedVariety.filteredVarieties,
    highlightedSeedVariety: seedVariety.highlightedVariety,
    onSeedVarietyHighlightChange: seedVariety.setHighlightedVariety,
    onConfirmSeedVariety: seedVariety.handleConfirmVariety,
    onActivateRow: (item, event) =>
      isSeedTab
        ? seedVariety.handleCropClick(item, event)
        : seedTreatment.handleRowKeyboardActivate(item, event),
    onCloseSeedFlyout: seedVariety.close,
    onAddSeedTreatment: seedTreatment.handleAdd,
    onCloseSeedTreatmentFlyout: seedTreatment.close,
    onClose,
    onSave: handleSave,
    onCommitSingleSelect: commitSingleSelect,
    // Checking via the keyboard (Enter in a preview, Ctrl/⌘+Enter) locks like a Ctrl/⌘+click.
    onToggleCheckedItem: (item) => {
      lockMultiSelect();
      toggleCheckedItem(item);
    },
    onEnterMultiSelectMode: selections.enterMultiSelectMode,
    onExitMultiSelectMode: selections.exitMultiSelectMode,
    onHighlightChange: setHighlightedItem,
    onCategoryChange: (group) => changeCategory(group, true),
  });

  usePickerFocusTrap(rootRef);

  // The Seed Treatment add list is a plain (non-virtualised) list, so it
  // pages the catalogue itself when scrolled near the bottom (OP-2401).
  const handleSeedTreatmentScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (
      scrollHeight - scrollTop - clientHeight < clientHeight &&
      catalogue.hasNextPage &&
      !catalogue.isFetchingNextPage &&
      !catalogue.hasFetchNextPageError
    ) {
      catalogue.fetchNextPage();
    }
  };

  // Crop and variety: "Add" rows in the crop list and the variety list's empty states.
  const isAddCropOffered =
    addRecordKind === 'crop' &&
    !!searchText.trim() &&
    !catalogue.isSearchFetching &&
    !catalogue.isLoading;
  const cropEmptyAction = isAddCropOffered ? (
    <AddRecordRow
      searchText={searchText}
      onClick={() => setAddRequest({ kind: 'crop', name: searchText })}
    />
  ) : undefined;
  const varietyEmptyAction =
    addRecordKind === 'crop' &&
    highlightedItem &&
    seedVariety.varietySearchText.trim() ? (
      <AddRecordRow
        searchText={seedVariety.varietySearchText}
        onClick={() =>
          setAddRequest({
            kind: 'variety',
            name: seedVariety.varietySearchText,
            crop: highlightedItem,
          })
        }
      />
    ) : undefined;

  const renderOptionsListContent = (): React.ReactNode => {
    if (!activeGroup) {
      return (
        <div className="p-4 text-sm text-gray-muted">
          {STEP_SELECTOR_NO_STEP_TYPES_MESSAGE}
        </div>
      );
    }

    if (isSeedTab) {
      return (
        <SeedCropVarietyPicker
          crops={filteredOptions}
          isMultiSelectMode={isMultiSelectMode}
          checkedItems={checkedItems}
          highlightedCrop={highlightedItem}
          isFlyoutOpen={isFlyoutOpen}
          highlightedVariety={seedVariety.highlightedVariety}
          varietySearchText={seedVariety.varietySearchText}
          decideLaterLabel={seedVariety.decideLaterLabel}
          anchorRef={rootRef}
          onCropClick={seedVariety.handleCropClick}
          onChangeVariety={seedVariety.openForCrop}
          onVarietySelect={seedVariety.handleVarietySelect}
          onDecideLater={seedVariety.handleDecideLater}
          onVarietySearchChange={seedVariety.setVarietySearchText}
          onCloseFlyout={seedVariety.close}
          cropEmptyAction={cropEmptyAction}
          varietyEmptyAction={varietyEmptyAction}
        />
      );
    }

    if (seedTreatmentTask && catalogue.isLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Spin size="small" />
        </div>
      );
    }

    if (seedTreatmentTask && filteredOptions.length === 0) {
      return (
        <div className="p-4 text-sm text-gray-muted">
          {STEP_SELECTOR_EMPTY_OPTIONS_MESSAGE}
        </div>
      );
    }

    if (seedTreatmentTask) {
      return (
        <SeedTreatmentProductPicker
          products={filteredOptions}
          task={seedTreatmentTask}
          gUnits={gUnits}
          anchorRef={rootRef}
          isMultiSelectMode={isMultiSelectMode}
          checkedItems={checkedItems}
          checkedSeeds={checkedSeeds}
          highlightedProduct={highlightedItem}
          isFlyoutOpen={isFlyoutOpen}
          draft={seedTreatment.draft}
          onDraftChange={seedTreatment.handleDraftChange}
          isFlyoutOpenedByKeyboard={seedTreatment.isOpenedByKeyboard}
          onProductClick={seedTreatment.handleRowMouseClick}
          onChangeDetails={seedTreatment.handleChangeDetails}
          onAdd={seedTreatment.handleAdd}
          onCloseFlyout={seedTreatment.close}
        />
      );
    }

    if (isCatalogueGroup && catalogue.isLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Spin size="small" />
        </div>
      );
    }

    if (isCatalogueGroup) {
      return (
        <>
          {catalogue.isSearchFetching && (
            <div className="flex flex-shrink-0 items-center gap-1.5 border-b border-gray-bright px-3 py-1.5 text-xs text-gray-muted">
              <Spin size="small" />
              <span>{STEP_SELECTOR_FETCHING_LABEL}</span>
            </div>
          )}
          <div className="flex-1 overflow-hidden">
            <CatalogueOptionList
              key={activeCategoryKey}
              items={filteredOptions}
              selectedGlobalId={highlightedItem?.globalId ?? null}
              onSelectItem={handleOptionClick}
              hasNextPage={catalogue.hasNextPage}
              fetchNextPage={catalogue.fetchNextPage}
              isFetchingNextPage={catalogue.isFetchingNextPage}
              hasFetchNextPageError={catalogue.hasFetchNextPageError}
              isMultiSelectMode={isMultiSelectMode}
              checkedGlobalIds={checkedGlobalIds}
              emptyAction={
                addRecordKind &&
                searchText.trim() &&
                !catalogue.isSearchFetching ? (
                  <AddRecordRow
                    searchText={searchText}
                    onClick={() =>
                      setAddRequest({
                        kind: addRecordKind,
                        name: searchText,
                      })
                    }
                  />
                ) : undefined
              }
            />
          </div>
        </>
      );
    }

    if (filteredOptions.length === 0) {
      return (
        <div className="p-4 text-sm text-gray-muted">
          {STEP_SELECTOR_EMPTY_OPTIONS_MESSAGE}
        </div>
      );
    }

    return filteredOptions.map((item) => {
      const isChecked = checkedItems.some((checked) =>
        stepOptionsMatch(checked, item)
      );
      const isHighlighted =
        !!highlightedItem && stepOptionsMatch(highlightedItem, item);
      return (
        <OptionRow
          key={`${item.item}-${item.globalId ?? 'none'}`}
          label={item.label}
          metaOption={item}
          isHighlighted={isHighlighted}
          isFilled={isMultiSelectMode ? isChecked : isHighlighted}
          showHighlightRing={isMultiSelectMode && isHighlighted}
          navAttributes={{
            [PICKER_OPTION_ID_ATTRIBUTE]: getStepOptionId(item),
          }}
          onClick={(event) => handleOptionClick(item, event)}
          leading={
            isMultiSelectMode && (
              <Checkbox checked={isChecked} className="mt-1" />
            )
          }
        />
      );
    });
  };

  // Only the Production tab: sized to its short list. With Operation too (a
  // Production task's costs, OP-2399) it keeps one compact height, so
  // switching tabs doesn't move it and push the tabs off-screen, without
  // leaving Production's few items in a full-height panel.
  const isProductionTask = taskType === InputTaskType.Production;
  const fitsContent = isProductionTask && groups.length <= 1;
  const heightClass = fitsContent
    ? 'max-h-[calc(100vh-20rem)]'
    : isProductionTask
      ? 'h-[min(28rem,calc(100vh-13rem))]'
      : 'h-[calc(100vh-13rem)]';

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      className={MergeClassNames(
        'flex outline-none w-fit min-w-[25rem] max-w-[calc(100vw-44rem)] flex-col bg-white p-3',
        heightClass,
        isFlyoutOpen || addRequest
          ? 'rounded-l-lg rounded-r-none'
          : 'rounded-lg'
      )}
    >
      <div onMouseDown={handleSearchMouseDown}>
        <SearchInput
          value={searchText}
          onChange={handleSearchChange}
          placeholder={STEP_SELECTOR_SEARCH_PLACEHOLDER}
        />
      </div>

      {/* A lone tab says nothing: limited to one category (categoryKeys) or
          a task that takes one, like an Other cost's Operation (OP-2399). */}
      {groups.length > 1 && (
        <CategoryTabStrip
          groups={groups}
          activeCategoryKey={activeCategoryKey}
          checkedCountByKey={checkedCountByKey}
          showCheckedCounts={isMultiSelectMode}
          lockedCategoryReason={lockedCategoryReason}
          lockedCategoryReasons={lockedCategoryReasons}
          onCategoryChange={(group) => changeCategory(group, false)}
        />
      )}

      {/* Stays mounted with multi-select so preventZoom covers the whole
          session. The hint and the selection chips share one grid cell, and
          a hidden hint keeps its space, so the option list below never
          shifts under the pointer as a multi-select starts (OP-2534). */}
      {enableMultiSelect && (
        <div className="mt-2 grid">
          <MultiSelectHint
            className={MergeClassNames(
              'col-start-1 row-start-1 w-full',
              (isMultiSelectMode || hasHadSelection) && 'invisible'
            )}
            preventZoom
          />
          {isMultiSelectMode && checkedItems.length > 0 && (
            <div className="col-start-1 row-start-1 min-w-0 self-center">
              <SelectionChips
                items={checkedItems}
                getItemLabel={
                  seedTreatmentTask
                    ? (item) =>
                        getSeedTreatmentOptionDisplayLabel(
                          item,
                          seedTreatmentTask,
                          checkedSeeds
                        )
                    : undefined
                }
                onRemoveItem={handleRemoveChip}
                singleLine
              />
            </div>
          )}
        </div>
      )}
      {!enableMultiSelect && isMultiSelectMode && checkedItems.length > 0 && (
        <SelectionChips
          items={checkedItems}
          getItemLabel={
            seedTreatmentTask
              ? (item) =>
                  getSeedTreatmentOptionDisplayLabel(
                    item,
                    seedTreatmentTask,
                    checkedSeeds
                  )
              : undefined
          }
          onRemoveItem={handleRemoveChip}
        />
      )}

      {isSeedTab && !enableMultiSelect ? (
        // Edit mode: two-panel layout so crops and varieties share the picker's width.
        <SeedVarietyTwoPanel
          crops={filteredOptions}
          highlightedCrop={highlightedItem}
          isSeedSelected={isFlyoutOpen}
          highlightedVariety={seedVariety.highlightedVariety}
          varietySearchText={seedVariety.varietySearchText}
          decideLaterLabel={seedVariety.decideLaterLabel}
          onCropClick={seedVariety.handleCropClick}
          onVarietySelect={seedVariety.handleVarietySelect}
          onDecideLater={seedVariety.handleDecideLater}
          onBackToSeeds={seedVariety.close}
          onVarietySearchChange={seedVariety.setVarietySearchText}
          cropEmptyAction={cropEmptyAction}
          varietyEmptyAction={varietyEmptyAction}
        />
      ) : seedTreatmentTask && !enableMultiSelect ? (
        // Edit mode: same two-panel layout, opening on the step's product.
        <SeedTreatmentTwoPanel
          products={filteredOptions}
          highlightedProduct={highlightedItem}
          isOpen={isFlyoutOpen}
          task={seedTreatmentTask}
          gUnits={gUnits}
          draft={seedTreatment.draft}
          onDraftChange={seedTreatment.handleDraftChange}
          onProductClick={(product) => seedTreatment.openForProduct(product)}
          onBackToProducts={seedTreatment.close}
          onSave={seedTreatment.handleSave}
        />
      ) : (
        <div
          ref={optionsContainerRef}
          className={MergeClassNames(
            'mt-3 flex-1 border border-gray-bright bg-white',
            isCatalogueGroup && !isSeedTab && !seedTreatmentTask
              ? 'flex flex-col overflow-hidden'
              : 'overflow-y-auto'
          )}
          onScroll={seedTreatmentTask ? handleSeedTreatmentScroll : undefined}
        >
          {renderOptionsListContent()}
        </div>
      )}

      {addRequest?.kind === 'variety' && (
        <AddVarietyFlyout
          anchorRef={rootRef}
          crop={addRequest.crop}
          initialName={addRequest.name}
          onClose={() => setAddRequest(null)}
          onCreated={(selection) => {
            setAddRequest(null);
            commitSingleSelect(selection);
          }}
        />
      )}
      {addRequest && addRequest.kind !== 'variety' && (
        <AddRecordFlyout
          anchorRef={rootRef}
          kind={addRequest.kind}
          initialName={addRequest.name}
          gUnits={gUnits}
          onClose={() => setAddRequest(null)}
          onCreated={(option) => {
            setAddRequest(null);
            commitSingleSelect(option);
          }}
        />
      )}

      {isMultiSelectMode && (
        <SelectionActionBar
          isSaveDisabled={isSaveDisabled}
          saveBlockedReason={saveBlockedReason}
          onCancel={handleCancel}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default StepInputSelector;
